<template>
  <div class="w-fit">
    <button
      class="transition-ease-in-out"
      :class="!isFirstPatientMode ? 'hover:opacity-70' : ''"
      v-show="canAddMorePatients || expand"
      @click="!isFirstPatientMode && togglePatient()"
    >
      <div class="flex space-x-6 h-[34px]">
        <div
          v-if="!expand"
          class="bg-primary-main rounded-full h-8 w-8 p-2 inline-flex"
        >
          <img src="@/assets/images/base/icons/plus.svg" loading="lazy" />
        </div>

        <div
          v-if="expand"
          class="rounded-3xl bg-primary-main h-8 w-[85px] pl-0 lg:pl-4 flex justify-end items-center transition-ease-in-out space-x-3 border border-primary-main"
        >
          <div
            class="flex items-center justify-center rounded-full w-[30px] h-[30px] shrink-0 bg-white"
          >
            <img src="@/assets/images/base/icons/check.svg" loading="lazy" />
          </div>
        </div>

        <div class="flex items-center space-x-2.5">
          <img :src="icon()" loading="lazy" />
          <span class="text-primary-main text-sm font-semibold">
            {{ age === "kid" ? $t("patients.kid") : $t("patients.adult") }}
          </span>
        </div>
      </div>
    </button>
  </div>

  <div class="pt-5">
    <AddPatientForm
      v-for="(patient, index) in agePatients"
      :ref="`patient${patient.id}`"
      :key="patient.id"
      :index="index"
      :patient="patient"
      :canDelete="agePatients.length > 1"
    />

    <button
      v-if="canAddMorePatients && expand && !isFirstPatientMode"
      @click="addPatient"
      class="inline-block text-sm text-primary-light font-semibold transition-ease-in-out hover:opacity-70 cursor-pointer mt-3 mb-6"
    >
      {{ age === "kid" ? $t("patients.add_kid") : $t("patients.add_adult") }}
    </button>

    <button
      v-if="isFirstPatientMode"
      class="inline-block text-sm text-primary-light font-semibold transition-ease-in-out hover:opacity-70 cursor-pointer mt-3 mb-6"
      @click="goToLoginPage"
    >
      {{ $t("auth.have_account") }} {{ $t("auth.sign_in") }}
    </button>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { v4 as uuidv4 } from "uuid";
import AddPatientForm from "@/components/Patient/Form/AddPatientForm.vue";

export default {
  name: "AddPatientSection",
  components: {
    AddPatientForm,
  },
  emits: ["addedPatient"],
  props: {
    age: {
      type: String,
      require: true,
      validator(value) {
        return ["kid", "adult"].includes(value);
      },
    },
    isFirstPatientMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
    };
  },
  computed: {
    canAddMorePatients() {
      return this.tmpPatients.length + this.patients.length < 5;
    },
    agePatients() {
      return this.tmpPatients.filter((item) => item.age.match(this.age));
    },
    hasAgePatients() {
      return this.agePatients.length > 0;
    },
    ...mapGetters({
      tmpPatients: "checkout/getTmpPatients",
      patients: "checkout/getPatients",
    }),
  },
  methods: {
    icon() {
      return require(`@/assets/images/base/icons/${this.age}.svg`);
    },
    goToLoginPage() {
      this.$router.push({ name: "LoginPatient" });
      this.clearTmpPatients();
    },
    defaultPatientData() {
      return {
        id: uuidv4(),
        first_name: "",
        last_name: "",
        pesel: "",
        isNoPesel: false,
        birth_date: "",
        sex: "",
        age: this.age,
        phone_number: "",
        country_code: "",
        country: "PL",
      };
    },
    togglePatient() {
      if (this.expand == false) {
        this.pushTmpPatient(this.defaultPatientData());
        this.$emit("addedPatient");
      } else {
        this.clearTmpPatients(this.age);
      }
      this.expand = !this.expand;
    },
    async addPatient() {
      const isValid = await this.validate();

      if (isValid) {
        this.pushTmpPatient(this.defaultPatientData());
      }
    },
    async validate() {
      let isValid = true;

      for (let patient of this.agePatients) {
        if (!(await this.$refs[`patient${patient.id}`][0].validate()))
          isValid = false;
      }

      return isValid;
    },
    ...mapMutations({
      pushTmpPatient: "checkout/pushTmpPatient",
      clearTmpPatients: "checkout/clearTmpPatients",
      closeModal: "modals/closeModal",
    }),
  },
  mounted() {
    this.expand = this.hasAgePatients;

    if (this.isFirstPatientMode) {
      this.expand = true;
      this.clearTmpPatients();
      this.pushTmpPatient(this.defaultPatientData());
    }
  },
};
</script>
